import { Color } from 'module/ui/components/styles';
import { Mark, mergeAttributes } from '@tiptap/core';

export type HighlightOptions = {
  HTMLAttributes: Record<string, any>;
};
type SelectionHighlightAttributes = HighlightOptions & {
  mimicSelection: true;
};
type NormalHighlightAttributes = HighlightOptions & {
  color: Color;
  mimicSelection: false;
};
export type HighlightAttributes =
  | SelectionHighlightAttributes
  | NormalHighlightAttributes;
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    highlight: {
      setHighlight: (attributes?: HighlightAttributes) => ReturnType;
      toggleHighlight: (attributes?: HighlightAttributes) => ReturnType;
      unsetHighlight: () => ReturnType;
    };
  }
}
export const isSelectionHighlight = (
  attrs: HighlightAttributes,
): attrs is SelectionHighlightAttributes => {
  return attrs.mimicSelection === true;
};
export const htmlAttributesToHighlightAttributes = (
  htmlAttributes: Record<string, any>,
): HighlightAttributes => {
  const mimicSelection = htmlAttributes['data-mimic-selection'];
  const color = htmlAttributes['data-color'];

  if (mimicSelection) {
    return {
      HTMLAttributes: htmlAttributes,
      mimicSelection: true,
    };
  }

  return {
    HTMLAttributes: htmlAttributes,
    color: color as Color,
    mimicSelection: false,
  };
};

export const HighlightMarkName = 'highlight';
export const Highlight = Mark.create<HighlightOptions, HighlightAttributes>({
  name: HighlightMarkName,
  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },
  addCommands() {
    return {
      setHighlight:
        (attributes) =>
        ({ commands }) => {
          return commands.setMark(this.name, attributes);
        },
      toggleHighlight:
        (attributes) =>
        ({ commands }) => {
          return commands.toggleMark(this.name, attributes);
        },
      unsetHighlight:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
    };
  },

  addAttributes() {
    return {
      mimicSelection: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-mimic-selection'),
        renderHTML: (attributes) => {
          return { ['data-mimic-selection']: attributes.mimicSelection };
        },
      },
      color: {
        default: 'yellow',
        parseHTML: (element) => element.getAttribute('data-color'),
        renderHTML: (attributes) => {
          if (!attributes.color) {
            return {};
          }

          return {
            ['data-color']: attributes.color,
          };
        },
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'mark',
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const attrs = htmlAttributesToHighlightAttributes(HTMLAttributes);
    const getColorStyle = () => {
      if (isSelectionHighlight(attrs)) {
        return `
           'background-color: var(--browser-selection-background);
           color: inherit;'
        `;
      }

      return `background-color: var(--ozone-${attrs.color}-surface);
            color: var(--ozone-${attrs.color});`;
    };
    const style = `${HTMLAttributes.style ?? ''}
        line-height: inherit;
        padding-left: 0;
        padding-right: 0;
        ${getColorStyle()}
      `;

    return [
      'mark',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        style,
      }),
      0,
    ];
  },
});
